<script setup lang="ts">
const { t, tm, rt } = useI18n()
const config = useRuntimeConfig()
const route  = useRoute()
const localePath = useLocalePath()

useReveal()

const siteUrl = config.public.siteUrl as string
const canonical = computed(() => {
  const path = route.path.endsWith('/') ? route.path : `${route.path}/`
  return `${siteUrl}${path}`
})

useHead(() => ({
  title: t('couriers.meta.title'),
  meta: [
    { name: 'description',        content: t('couriers.meta.description') },
    { property: 'og:title',       content: t('couriers.meta.title') },
    { property: 'og:description', content: t('couriers.meta.description') },
    { property: 'og:url',         content: canonical.value },
    { name: 'twitter:title',       content: t('couriers.meta.title') },
    { name: 'twitter:description', content: t('couriers.meta.description') },
  ],
  link: [{ rel: 'canonical', href: canonical.value }],
}))

type EarningItem  = { icon: string; value: string; label: string; sub: string }
type ProcessItem  = { n: string; title: string; text: string }
type ReqItem      = { icon: string; title: string; text: string }
type FaqItem      = { q: string; a: string }

const earnings = computed(() =>
  (tm('couriers.earnings.items') as any[]).map((it) => ({
    icon: rt(it.icon), value: rt(it.value), label: rt(it.label), sub: rt(it.sub),
  })) as EarningItem[],
)
const process = computed(() =>
  (tm('how.courier') as any[]).map((it) => ({
    n: rt(it.n), title: rt(it.title), text: rt(it.text),
  })) as ProcessItem[],
)
const requirements = computed(() =>
  (tm('couriers.requirements.items') as any[]).map((it) => ({
    icon: rt(it.icon), title: rt(it.title), text: rt(it.text),
  })) as ReqItem[],
)
const faq = computed(() =>
  (tm('couriers.faq.items') as any[]).map((it) => ({
    q: rt(it.q), a: rt(it.a),
  })) as FaqItem[],
)
</script>

<template>
  <div>
    <SiteNav />

    <main>
      <PageHero
        :eyebrow="t('couriers.hero.eyebrow')"
        :title="t('couriers.hero.title')"
        :lead="t('couriers.hero.lead')"
        :cta-primary-label="t('couriers.hero.ctaPrimary')"
        cta-primary-href="#cta"
        :cta-secondary-label="t('couriers.hero.ctaSecondary')"
        :cta-secondary-href="`${localePath('/')}#earnings`"
      />

      <!-- Earnings panel -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5"
                 style="background: rgba(20,197,219,0.10); border-color: rgba(20,197,219,0.30); color: var(--color-cyan-300);">
              💼 {{ t('couriers.earnings.eyebrow') }}
            </div>
            <h2 class="rod-h2">{{ t('couriers.earnings.title') }}</h2>
          </div>
          <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div v-for="(e, i) in earnings" :key="i" class="rod-card flex flex-col gap-3" data-reveal>
              <div class="grid place-items-center w-12 h-12 rounded-2xl"
                   style="background: linear-gradient(140deg, rgba(34,211,238,0.16) 0%, rgba(20,197,219,0.08) 100%);
                          border: 1px solid rgba(20,197,219,0.25);
                          font-size: 22px;">
                {{ e.icon }}
              </div>
              <div class="font-display text-3xl rod-cyan-text font-bold tracking-tight">{{ e.value }}</div>
              <div class="text-sm uppercase tracking-wider text-[var(--color-text)]">{{ e.label }}</div>
              <div class="text-xs text-[var(--color-text-dim)] leading-relaxed">{{ e.sub }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Process (reuses home keys) -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5"
                 style="background: rgba(20,197,219,0.10); border-color: rgba(20,197,219,0.30); color: var(--color-cyan-300);">
              🛠 {{ t('how.eyebrow') }}
            </div>
            <h2 class="rod-h2" v-html="t('how.titleA')" />
          </div>
          <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div
              v-for="(item, i) in process"
              :key="i"
              class="rod-card flex flex-col gap-3"
              data-reveal
            >
              <div class="font-mono text-base font-bold rod-cyan-text">{{ item.n }}</div>
              <h3 class="rod-h3 leading-tight" style="font-size: 1.1rem;">{{ item.title }}</h3>
              <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.92rem]">{{ item.text }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Requirements -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5"
                 style="background: rgba(20,197,219,0.10); border-color: rgba(20,197,219,0.30); color: var(--color-cyan-300);">
              ✅ {{ t('couriers.requirements.eyebrow') }}
            </div>
            <h2 class="rod-h2">{{ t('couriers.requirements.title') }}</h2>
          </div>
          <div class="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <div
              v-for="(item, i) in requirements"
              :key="i"
              class="rod-card flex gap-4"
              data-reveal
            >
              <div class="grid place-items-center w-12 h-12 rounded-2xl flex-shrink-0 mt-1"
                   style="background: linear-gradient(140deg, rgba(34,211,238,0.16) 0%, rgba(20,197,219,0.08) 100%);
                          border: 1px solid rgba(20,197,219,0.25);
                          font-size: 22px;">
                {{ item.icon }}
              </div>
              <div>
                <h3 class="rod-h3 leading-tight mb-2" style="font-size: 1.05rem;">{{ item.title }}</h3>
                <p class="text-[var(--color-text-muted)] leading-relaxed text-[0.92rem]">{{ item.text }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="rod-section">
        <div class="rod-container">
          <div class="text-center max-w-3xl mx-auto mb-12" data-reveal>
            <div class="rod-eyebrow mb-5"
                 style="background: rgba(20,197,219,0.10); border-color: rgba(20,197,219,0.30); color: var(--color-cyan-300);">
              ❓ {{ t('couriers.faq.eyebrow') }}
            </div>
            <h2 class="rod-h2">{{ t('couriers.faq.title') }}</h2>
          </div>
          <div class="max-w-3xl mx-auto flex flex-col gap-3">
            <details v-for="(item, i) in faq" :key="i" class="rod-card group" data-reveal>
              <summary class="cursor-pointer list-none flex items-start justify-between gap-4">
                <span class="rod-h3 leading-snug" style="font-size: 1.05rem;">{{ item.q }}</span>
                <span class="text-[var(--color-cyan-300)] text-2xl leading-none flex-shrink-0 transition-transform duration-200 group-open:rotate-45">+</span>
              </summary>
              <p class="mt-4 text-[var(--color-text-muted)] leading-relaxed text-[0.95rem]">{{ item.a }}</p>
            </details>
          </div>
        </div>
      </section>

      <!-- CTA -->
      <section id="cta" class="rod-section relative overflow-hidden">
        <div class="rod-aurora" />
        <div class="rod-container relative">
          <div class="rod-card rod-card--glow text-center py-16 px-6 lg:px-12 max-w-4xl mx-auto" data-reveal
               style="background: linear-gradient(135deg, rgba(20,197,219,0.14) 0%, rgba(31,215,154,0.06) 100%);
                      border-color: rgba(20,197,219,0.35);">
            <h2 class="rod-h2 mb-5 rod-cyan-text">{{ t('couriers.cta.title') }}</h2>
            <p class="text-lg text-[var(--color-text-muted)] max-w-2xl mx-auto mb-10">{{ t('couriers.cta.lead') }}</p>
            <div class="flex flex-wrap justify-center gap-4">
              <a :href="`mailto:hello@romba.am?subject=${encodeURIComponent(t('cta.mailSubject'))}`" class="rod-btn-primary">{{ t('couriers.cta.primary') }} <span aria-hidden="true">→</span></a>
              <a :href="`${localePath('/')}#earnings`" class="rod-btn-secondary">{{ t('couriers.cta.secondary') }}</a>
            </div>
          </div>
        </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>
